import {
  columnVisibilityFeature,
  createColumnHelper,
  metaHelper,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';

export interface DataTableColumnMeta {
  /** Name in menus (column visibility) when the header isn't plain text. */
  label?: string;
  align?: 'left' | 'right' | 'center';
  /**
   * The server's field name for sorting. Only columns with one are sortable: the server
   * silently ignores sorts outside its allow-list, so everything else stays inert.
   */
  sortField?: string;
  /** Default true. The column identifying the row should set false. */
  hideable?: boolean;
  /** Starts hidden; the user can turn it on in the view options. */
  hiddenByDefault?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
  columnVisibilityFeature,
  rowSelectionFeature,
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous accessor values, as TanStack's own helper types them
export type DataTableColumnDef<TData extends RowData> = ColumnDef<DataTableFeatures, TData, any>;

/** Column helper bound to the shared feature set. Use `.columns([...])` to keep value types. */
export const createDataTableColumns = <TData extends RowData>() => createColumnHelper<DataTableFeatures, TData>();

/** Checkbox column for bulk selection. `label` names the row for screen readers. */
export function selectColumn<TData extends RowData>(label: (row: TData) => string): DataTableColumnDef<TData> {
  return {
    id: 'select',
    meta: { hideable: false, label: 'Select', headerClassName: 'w-10', cellClassName: 'w-10' },
    header: ({ table }) => (
      <Checkbox
        aria-label="Select all rows on this page"
        checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label={`Select ${label(row.original)}`}
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(value === true)}
      />
    ),
  };
}
